"use client"

import { useState } from "react"
import {
  Download,
  Save,
  FileText,
  Heart,
  BarChart3,
  RefreshCw,
  Users,
  Brain,
  Building2,
  TrendingUp,
  Target,
  Zap,
} from "lucide-react"
import "./department-department-detailed.css"
import BackButton from "../../shared/backButton"
import Modal from "../../layout/Modal"

const DepartmentDetailedReport = ({ comparisonData }) => {
  console.log(comparisonData, "comparisonData from <DepartmentDetailedReport />")
  const [activeSection, setActiveSection] = useState("overview")
  const [activeSubTab, setActiveSubTab] = useState("analysis")
  const [activeSkillTab, setActiveSkillTab] = useState("softSkills")
  const [isSaving, setIsSaving] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showFilenameModal, setShowFilenameModal] = useState(false)
  const [filename, setFilename] = useState("")

  const dd = comparisonData?.detailedReport || {}
  const deptA = dd?.departmentDetails?.deptA || {}
  const deptB = dd?.departmentDetails?.deptB || {}
  const mobility = dd?.inernalMobilityMetrics || dd?.internalMobilityMetrics || {}
  const mobilityViz = mobility?.visualization || {}
  const upReadiness = dd?.upskillingReadiness || {}
  const upViz = upReadiness?.visualization || {}
  const aReadiness = upViz?.deptAUpskillingReadinessData || {}
  const bReadiness = upViz?.deptBUpskillingReadinessData || {}
  const aCounts = aReadiness?.upskillingData || {}
  const bCounts = bReadiness?.upskillingData || {}

    // ---- Skill Gap helpers & selectors (from backend) ----
  const clampPct = (v) => Math.max(0, Math.min(100, Number.isFinite(v) ? v : 0))

  // Overview averages (Soft/Hard %) come from detailedReport.overview
  const overview = dd?.overview || {}
  const aSoft = Number(overview.deptAAvgSoftSkillGap ?? 0)
  const bSoft = Number(overview.deptBAvgSoftSkillGap ?? 0)
  const aHard = Number(overview.deptAAvgHardSkillGap ?? 0)
  const bHard = Number(overview.deptBAvgHardSkillGap ?? 0)

  // Per-skill visualization comes from detailedReport.aggregateSkillGaps.visualization
  const aggViz = dd?.aggregateSkillGaps?.visualization || {}
  const softSkills = Array.isArray(aggViz.softSkills) ? aggViz.softSkills : []
  const hardSkills = Array.isArray(aggViz.hardSkills) ? aggViz.hardSkills : []

  const deptAName = deptA?.name || "Department A"
  const deptBName = deptB?.name || "Department B"

  // Hard skill chip lists per department
  const hardAList = hardSkills.find((h) => h?.name === deptAName)?.gaps || []
  const hardBList = hardSkills.find((h) => h?.name === deptBName)?.gaps || []


  /* Dummy sample removed in favor of backend data
  const departmentData = { ...hardcoded... }
  */
  const departmentData = {
    title: "Comprehensive Department Analysis Report",
    departments: [
      {
        name: deptA?.name || "Department A",
        size: Number(deptA?.size ?? dd?.overview?.deptASize ?? 0),
        roles: [], // API does not provide roles list in current payload
        softSkills: [],
        hardSkills: [],
        description: "",
        avgMobilityScore:
          Number(deptA?.avgMobilityScore ?? mobilityViz?.deptAAvgMaxMobilityScore ?? 0) /
          (mobilityViz?.deptAAvgMaxMobilityScore ? 10 : 10), // maintain 0-10 scale if you wish; we will use percentage bars directly elsewhere
        mobilityOpportunities: Number(deptA?.mobilityOpportunities ?? dd?.overview?.deptAMobilityOpportunities ?? 0),
        upskillReadiness: {
          critical: Number(aCounts?.Critical ?? 0),
          high: Number(aCounts?.High ?? 0),
          moderate: Number(aCounts?.Moderate ?? 0),
          low: Number(aCounts?.Low ?? 0),
        },
      },
      {
        name: deptB?.name || "Department B",
        size: Number(deptB?.size ?? dd?.overview?.deptBSize ?? 0),
        roles: [],
        softSkills: [],
        hardSkills: [],
        description: "",
        avgMobilityScore:
          Number(deptB?.avgMobilityScore ?? mobilityViz?.deptBAvgMaxMobilityScore ?? 0) /
          (mobilityViz?.deptBAvgMaxMobilityScore ? 10 : 10),
        mobilityOpportunities: Number(deptB?.mobilityOpportunities ?? dd?.overview?.deptBMobilityOpportunities ?? 0),
        upskillReadiness: {
          critical: Number(bCounts?.Critical ?? 0),
          high: Number(bCounts?.High ?? 0),
          moderate: Number(bCounts?.Moderate ?? 0),
          low: Number(bCounts?.Low ?? 0),
        },
      },
    ],
    skillGaps: {
      softSkills: [],
      hardSkills: [],
    },
  }

  const sections = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "departmentDetails", label: "Department Details", icon: Building2 },
    { id: "internalMobility", label: "Internal Mobility Metrics", icon: TrendingUp },
    { id: "aggregateSkillGaps", label: "Aggregate Skill Gaps", icon: Target },
    { id: "upskillReadiness", label: "Upskilling Readiness", icon: Zap },
    { id: "summary", label: "Summary", icon: FileText },
  ]

  const renderSubTabs = (sectionId) => {
    if (sectionId === "aggregateSkillGaps") {
      return (
        <div className="dept-detailed-sub-tabs">
          <button
            className={`dept-detailed-sub-tab ${activeSubTab === "analysis" ? "active" : ""}`}
            onClick={() => setActiveSubTab("analysis")}
          >
            Analysis
          </button>
          <button
            className={`dept-detailed-sub-tab ${activeSubTab === "visualization" ? "active" : ""}`}
            onClick={() => setActiveSubTab("visualization")}
          >
            Visualization
          </button>
        </div>
      )
    }

    if (["internalMobility", "upskillReadiness"].includes(sectionId)) {
      return (
        <div className="dept-detailed-sub-tabs">
          <button
            className={`dept-detailed-sub-tab ${activeSubTab === "analysis" ? "active" : ""}`}
            onClick={() => setActiveSubTab("analysis")}
          >
            Analysis
          </button>
          <button
            className={`dept-detailed-sub-tab ${activeSubTab === "visualization" ? "active" : ""}`}
            onClick={() => setActiveSubTab("visualization")}
          >
            Visualization
          </button>
        </div>
      )
    }

    return null
  }

  const renderSkillTabs = () => {
    return (
      <div className="dept-detailed-skill-tabs">
        <button
          className={`dept-detailed-skill-tab ${activeSkillTab === "softSkills" ? "active" : ""}`}
          onClick={() => setActiveSkillTab("softSkills")}
        >
          Soft Skills
        </button>
        <button
          className={`dept-detailed-skill-tab ${activeSkillTab === "hardSkills" ? "active" : ""}`}
          onClick={() => setActiveSkillTab("hardSkills")}
        >
          Hard Skills
        </button>
      </div>
    )
  }

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="dept-detailed-section-content">
            <div className="dept-detailed-overview-grid">
              {/* Department A Size */}
              <div className="dept-detailed-overview-card dept-detailed-primary">
                <div className="dept-detailed-overview-icon">
                  <Users size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Size</h3>
                  <div className="dept-detailed-score-display">{departmentData.departments[0].size} People</div>
                  <p>in {departmentData.departments[0].name}</p>
                </div>
              </div>

              {/* Department B Size */}
              <div className="dept-detailed-overview-card dept-detailed-secondary">
                <div className="dept-detailed-overview-icon">
                  <Users size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Size</h3>
                  <div className="dept-detailed-score-display">{departmentData.departments[1].size} People</div>
                  <p>in {departmentData.departments[1].name}</p>
                </div>
              </div>

              {/* Department A Mobility Opportunities */}
              <div className="dept-detailed-overview-card dept-detailed-accent">
                <div className="dept-detailed-overview-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Mobility Opportunities</h3>
                  <div className="dept-detailed-score-display">
                    {departmentData.departments[0].mobilityOpportunities} Positions
                  </div>
                  <p>Available in {departmentData.departments[0].name}</p>
                </div>
              </div>

              {/* Department B Mobility Opportunities */}
              <div className="dept-detailed-overview-card dept-detailed-success">
                <div className="dept-detailed-overview-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Mobility Opportunities</h3>
                  <div className="dept-detailed-score-display">
                    {departmentData.departments[1].mobilityOpportunities} Positions
                  </div>
                  <p>Available in {departmentData.departments[1].name}</p>
                </div>
              </div>

              {/* Soft/Hard Skill Gaps (averages from backend, shown as %) */}
              <div className="dept-detailed-overview-card dept-detailed-info">
                <div className="dept-detailed-overview-icon">
                  <Heart size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Soft Skill Gap</h3>
                  <div className="dept-detailed-score-display">{`${aSoft.toFixed(2)}%`}</div>
                  {/*<div className="dept-detailed-progress-container">
                    <div className="dept-detailed-progress-bar">
                      <div
                        className="dept-detailed-progress-fill dept-detailed-dept1"
                        style={{ width: `${clampPct(aSoft)}%` }}
                      />
                    </div>
                  </div>*/}
                  <p>{deptAName}</p>
                </div>
              </div>

              <div className="dept-detailed-overview-card dept-detailed-warning">
                <div className="dept-detailed-overview-icon">
                  <Heart size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Soft Skill Gap</h3>
                  <div className="dept-detailed-score-display">{`${bSoft.toFixed(2)}%`}</div>
                  {/*<div className="dept-detailed-progress-container">
                    <div className="dept-detailed-progress-bar">
                      <div
                        className="dept-detailed-progress-fill dept-detailed-dept2"
                        style={{ width: `${clampPct(bSoft)}%` }}
                      />
                    </div>
                  </div>*/}
                  <p>{deptBName}</p>
                </div>
              </div>

              <div className="dept-detailed-overview-card dept-detailed-danger">
                <div className="dept-detailed-overview-icon">
                  <Brain size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Hard Skill Gap</h3>
                  <div className="dept-detailed-score-display">{`${aHard.toFixed(2)}%`}</div>
                  {/*<div className="dept-detailed-progress-container">
                    <div className="dept-detailed-progress-bar">
                      <div
                        className="dept-detailed-progress-fill dept-detailed-dept1"
                        style={{ width: `${clampPct(aHard)}%` }}
                      />
                    </div>
                  </div>*/}
                  <p>{deptAName}</p>
                </div>
              </div>

              <div className="dept-detailed-overview-card dept-detailed-dark">
                <div className="dept-detailed-overview-icon">
                  <Brain size={24} />
                </div>
                <div className="dept-detailed-overview-content">
                  <h3>Hard Skill Gap</h3>
                  <div className="dept-detailed-score-display">{`${bHard.toFixed(2)}%`}</div>
                  {/*<div className="dept-detailed-progress-container">
                    <div className="dept-detailed-progress-bar">
                      <div
                        className="dept-detailed-progress-fill dept-detailed-dept2"
                        style={{ width: `${clampPct(bHard)}%` }}
                      />
                    </div>
                  </div>*/}
                  <p>{deptBName}</p>
                </div>
              </div>

            </div>
          </div>
        )

      case "departmentDetails":
        return (
          <div className="dept-detailed-section-content">
            <h3 style={{ marginBottom: "10px" }}>Department Details</h3>
            <p style={{ marginBottom: "20px" }}>
              Comprehensive department profiles including size, roles, skills, and descriptions.
            </p>
            <div className="dept-detailed-department-grid">
              {departmentData.departments.map((dept, index) => (
                <div key={index} className="dept-detailed-department-card">
                  <div className="dept-detailed-department-header">
                    <h4>{dept.name}</h4>
                  </div>
                  <div className="dept-detailed-department-info">
                    <div className="dept-detailed-info-item">
                      <span className="dept-detailed-info-label">Department Size:</span>
                      <span className="dept-detailed-info-value">{dept.size} people</span>
                    </div>
                    <div className="dept-detailed-info-item">
                      <span className="dept-detailed-info-label">Number of Roles:</span>
                      <span className="dept-detailed-info-value">{dept.roles.length} roles</span>
                    </div>
                    <div className="dept-detailed-info-item">
                      <span className="dept-detailed-info-label">Avg Mobility Score:</span>
                      {/* Keep existing label; show as percentage if available */}
                      <span className="dept-detailed-info-value">
                        {isFinite(dept.avgMobilityScore) ? dept.avgMobilityScore : "N/A"}
                      </span>
                    </div>
                    <div className="dept-detailed-info-item">
                      <span className="dept-detailed-info-label">Mobility Opportunities:</span>
                      <span className="dept-detailed-info-value">{dept.mobilityOpportunities}</span>
                    </div>
                  </div>
                  <div className="dept-detailed-skills-section">
                    <div className="dept-detailed-skill-category">
                      <span className="dept-detailed-info-label">Key Roles:</span>
                      <div className="dept-detailed-skill-tags">
                        {dept.roles.slice(0, 5).map((role, idx) => (
                          <span key={idx} className="dept-detailed-skill-tag">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case "internalMobility":
  return (
    <div className="dept-detailed-section-content">
      {renderSubTabs("internalMobility")}
      {activeSubTab === "analysis" ? (
        <div className="dept-detailed-analysis-container">
          <h3>Internal Mobility Analysis</h3>
          <div className="dept-detailed-analysis-content">
            <p>{mobility?.analysis ?? "No analysis available."}</p>
          </div>
        </div>
      ) : (
        <div>
          <h3 style={{ marginBottom: "10px" }}>Internal Mobility Metrics Comparison</h3>
          <p style={{ marginBottom: "20px" }}>
            Comparison of department sizes, mobility opportunities, and average maximum internal mobility scores.
          </p>

          <div className="dept-detailed-mobility-grid">
            {/* Average Maximum Internal Mobility Score */}
            <div className="dept-detailed-mobility-card">
              <h4>Average Maximum Internal Mobility Score</h4>
              <div className="dept-detailed-comparison-bars">
                {/* Dept A */}
                <div className="dept-detailed-department-bar">
                  <span className="dept-detailed-department-label">
                    {departmentData.departments[0]?.name ?? "Department A"}
                  </span>
                  <div className="dept-detailed-progress-container">
                    <div className="dept-detailed-progress-bar">
                      <div
                        className="dept-detailed-progress-fill dept-detailed-dept1"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(100, Number(mobilityViz?.deptAAvgMaxMobilityScore ?? 0))
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="dept-detailed-score">
                      {Number.isFinite(Number(mobilityViz?.deptAAvgMaxMobilityScore))
                        ? `${Number(mobilityViz?.deptAAvgMaxMobilityScore).toFixed(2)}%`
                        : "0.00%"}
                    </span>
                  </div>
                </div>

                {/* Dept B */}
                <div className="dept-detailed-department-bar">
                  <span className="dept-detailed-department-label">
                    {departmentData.departments[1]?.name ?? "Department B"}
                  </span>
                  <div className="dept-detailed-progress-container">
                    <div className="dept-detailed-progress-bar">
                      <div
                        className="dept-detailed-progress-fill dept-detailed-dept2"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(100, Number(mobilityViz?.deptBAvgMaxMobilityScore ?? 0))
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="dept-detailed-score">
                      {Number.isFinite(Number(mobilityViz?.deptBAvgMaxMobilityScore))
                        ? `${Number(mobilityViz?.deptBAvgMaxMobilityScore).toFixed(2)}%`
                        : "0.00%"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Department Size Comparison */}
            <div className="dept-detailed-mobility-card">
              <h4>Department Size Comparison</h4>
              <div className="dept-detailed-comparison-bars">
                {(() => {
                  const sizeA = Number(departmentData.departments[0]?.size ?? 0);
                  const sizeB = Number(departmentData.departments[1]?.size ?? 0);
                  const denom = Math.max(1, sizeA + sizeB);
                  const pctA = (sizeA / denom) * 100;
                  const pctB = (sizeB / denom) * 100;

                  return (
                    <>
                      <div className="dept-detailed-department-bar">
                        <span className="dept-detailed-department-label">
                          {departmentData.departments[0]?.name ?? "Department A"}
                        </span>
                        <div className="dept-detailed-progress-container">
                          <div className="dept-detailed-progress-bar">
                            <div
                              className="dept-detailed-progress-fill dept-detailed-dept1"
                              style={{ width: `${pctA}%` }}
                            />
                          </div>
                          <span className="dept-detailed-score">{sizeA}</span>
                        </div>
                      </div>

                      <div className="dept-detailed-department-bar">
                        <span className="dept-detailed-department-label">
                          {departmentData.departments[1]?.name ?? "Department B"}
                        </span>
                        <div className="dept-detailed-progress-container">
                          <div className="dept-detailed-progress-bar">
                            <div
                              className="dept-detailed-progress-fill dept-detailed-dept2"
                              style={{ width: `${pctB}%` }}
                            />
                          </div>
                          <span className="dept-detailed-score">{sizeB}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

      case "aggregateSkillGaps":
        return (
          <div className="dept-detailed-section-content">
            {renderSubTabs("aggregateSkillGaps")}
            {activeSubTab === "analysis" ? (
              <div className="dept-detailed-analysis-container">
                <h3>Aggregate Skill Gaps Analysis</h3>
                <div className="dept-detailed-analysis-content">
                  <div className="dept-detailed-analysis-section">
                    <h4>Soft Skills Analysis</h4>
                    <p>{dd?.aggregateSkillGaps?.analysis || "No analysis available."}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Skill Gaps Comparison</h3>
                <p style={{ marginBottom: "20px" }}>
                  Department-level skill gap analysis showing soft and hard skill proficiency levels.
                </p>
                {renderSkillTabs()}

                {/* Soft Skills (per-skill bars by department) and Hard Skills (chip lists) */}
                {activeSkillTab === "softSkills" ? (
                  <div className="dept-detailed-skills-grid">
                  {softSkills.length === 0 ? (
                    <p>No soft skill gap data available.</p>
                  ) : (
                    softSkills.map((row) => {
                      const a = (row.departments || []).find((d) => d?.name === deptAName);
                      const b = (row.departments || []).find((d) => d?.name === deptBName);
                      const aPct = clampPct(Number(a?.gapPercentage ?? 0));
                      const bPct = clampPct(Number(b?.gapPercentage ?? 0));

                      return (
                        <div key={row.skill} className="dept-detailed-skill-card">
                          <div className="dept-detailed-skill-header">
                            <h4 style={{ marginBottom: "8px" }}>{row.skill}</h4>
                          </div>

                          <div className="dept-detailed-comparison-bars">
                            {/* Dept A bar */}
                            <div className={`dept-detailed-department-bar ${a?.isGap ? "dept-detailed-has-gap" : ""}`}>
                              <span className="dept-detailed-department-label">
                                {deptAName}
                                {a?.isGap && <span className="dept-detailed-gap-indicator">GAP</span>}
                              </span>
                              <div className="dept-detailed-progress-container">
                                <div className="dept-detailed-progress-bar">
                                  <div
                                    className="dept-detailed-progress-fill dept-detailed-dept1"
                                    style={{ width: `${aPct}%` }}
                                  />
                                </div>
                                <span className="dept-detailed-score">{aPct.toFixed(2)}%</span>
                              </div>
                            </div>

                            {/* Dept B bar */}
                            <div className={`dept-detailed-department-bar ${b?.isGap ? "dept-detailed-has-gap" : ""}`}>
                              <span className="dept-detailed-department-label">
                                {deptBName}
                                {b?.isGap && <span className="dept-detailed-gap-indicator">GAP</span>}
                              </span>
                              <div className="dept-detailed-progress-container">
                                <div className="dept-detailed-progress-bar">
                                  <div
                                    className="dept-detailed-progress-fill dept-detailed-dept2"
                                    style={{ width: `${bPct}%` }}
                                  />
                                </div>
                                <span className="dept-detailed-score">{bPct.toFixed(2)}%</span>
                              </div>
                            </div>

                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                ) : (
                  <div className="dept-detailed-hard-gaps">
                    <div className="dept-detailed-skill-card">
                      <div className="dept-detailed-skill-header">
                        <h4 style={{ marginBottom: "12px" }}>{deptAName}</h4>
                      </div>
                      {hardAList.length === 0 ? (
                        <p>No hard skill gaps found.</p>
                      ) : (
                        <div className="dept-detailed-skill-tags">
                          {hardAList.map((s) => (
                            <span key={`a-${s}`} className="dept-detailed-skill-tag">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="dept-detailed-skill-card">
                      <div className="dept-detailed-skill-header">
                        <h4 style={{ marginBottom: "12px" }}>{deptBName}</h4>
                      </div>
                      {hardBList.length === 0 ? (
                        <p>No hard skill gaps found.</p>
                      ) : (
                        <div className="dept-detailed-skill-tags">
                          {hardBList.map((s) => (
                            <span key={`b-${s}`} className="dept-detailed-skill-tag">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        )

      case "upskillReadiness":
        return (
          <div className="dept-detailed-section-content">
            {renderSubTabs("upskillReadiness")}
            {activeSubTab === "analysis" ? (
              <div className="dept-detailed-analysis-container">
                <h3>Upskilling Readiness Analysis</h3>
                <div className="dept-detailed-analysis-content">
                  <p>{upReadiness?.analysis || "No analysis available."}</p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Upskilling Readiness Comparison</h3>
                <p style={{ marginBottom: "20px" }}>
                  Employee distribution across upskilling readiness categories and average readiness scores by
                  department.
                </p>
                <div className="dept-detailed-upskill-grid">
                  {/* Department A Readiness Cards */}
                  <div className="dept-detailed-readiness-section">
                    <h4>{departmentData.departments[0].name} Readiness Distribution</h4>
                    <div className="dept-detailed-readiness-cards">
                      <div className="dept-detailed-readiness-card dept-detailed-critical">
                        <div className="dept-detailed-readiness-content">
                          <h5>Critical</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[0].upskillReadiness.critical}
                          </div>
                        </div>
                      </div>
                      <div className="dept-detailed-readiness-card dept-detailed-high">
                        <div className="dept-detailed-readiness-content">
                          <h5>High</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[0].upskillReadiness.high}
                          </div>
                        </div>
                      </div>
                      <div className="dept-detailed-readiness-card dept-detailed-moderate">
                        <div className="dept-detailed-readiness-content">
                          <h5>Moderate</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[0].upskillReadiness.moderate}
                          </div>
                        </div>
                      </div>
                      <div className="dept-detailed-readiness-card dept-detailed-low">
                        <div className="dept-detailed-readiness-content">
                          <h5>Low</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[0].upskillReadiness.low}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Department B Readiness Cards */}
                  <div className="dept-detailed-readiness-section">
                    <h4>{departmentData.departments[1].name} Readiness Distribution</h4>
                    <div className="dept-detailed-readiness-cards">
                      <div className="dept-detailed-readiness-card dept-detailed-critical">
                        <div className="dept-detailed-readiness-content">
                          <h5>Critical</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[1].upskillReadiness.critical}
                          </div>
                        </div>
                      </div>
                      <div className="dept-detailed-readiness-card dept-detailed-high">
                        <div className="dept-detailed-readiness-content">
                          <h5>High</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[1].upskillReadiness.high}
                          </div>
                        </div>
                      </div>
                      <div className="dept-detailed-readiness-card dept-detailed-moderate">
                        <div className="dept-detailed-readiness-content">
                          <h5>Moderate</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[1].upskillReadiness.moderate}
                          </div>
                        </div>
                      </div>
                      <div className="dept-detailed-readiness-card dept-detailed-low">
                        <div className="dept-detailed-readiness-content">
                          <h5>Low</h5>
                          <div className="dept-detailed-readiness-count">
                            {departmentData.departments[1].upskillReadiness.low}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Average Readiness Score Comparison */}
                  <div className="dept-detailed-avg-readiness">
                    <h4>Average Upskilling Readiness Score</h4>
                    <div className="dept-detailed-comparison-bars">
                      <div className="dept-detailed-department-bar">
                        <span className="dept-detailed-department-label">{departmentData.departments[0].name}</span>
                        <div className="dept-detailed-progress-container">
                          <div className="dept-detailed-progress-bar">
                            <div
                              className="dept-detailed-progress-fill dept-detailed-dept1"
                              style={{ width: `${Math.max(0, Math.min(100, Number(aReadiness?.avgScore ?? 0)))}%` }}
                            ></div>
                          </div>
                          <span className="dept-detailed-score">
                            {Number.isFinite(Number(aReadiness?.avgScore))
                              ? `${Number(aReadiness.avgScore).toFixed(2)}%`
                              : "0.00%"}
                          </span>
                        </div>
                      </div>
                      <div className="dept-detailed-department-bar">
                        <span className="dept-detailed-department-label">{departmentData.departments[1].name}</span>
                        <div className="dept-detailed-progress-container">
                          <div className="dept-detailed-progress-bar">
                            <div
                              className="dept-detailed-progress-fill dept-detailed-dept2"
                              style={{ width: `${Math.max(0, Math.min(100, Number(bReadiness?.avgScore ?? 0)))}%` }}
                            ></div>
                          </div>
                          <span className="dept-detailed-score">
                            {Number.isFinite(Number(bReadiness?.avgScore))
                              ? `${Number(bReadiness.avgScore).toFixed(2)}%`
                              : "0.00%"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )

      case "summary":
        return (
          <div className="dept-detailed-section-content">
            <div className="dept-detailed-summary-content">
              <div className="dept-detailed-summary-section">
                <h4>Internal Mobility Metrics Summary</h4>
                <p>{dd?.summary?.mobilityMetrics || "No summary available."}</p>
              </div>
              <div className="dept-detailed-summary-section">
                <h4>Aggregate Skill Gaps Summary</h4>
                <p>{dd?.summary?.skillGaps || "No summary available."}</p>
              </div>
              <div className="dept-detailed-summary-section">
                <h4>Upskilling Readiness Summary</h4>
                <p>{dd?.summary?.upskillingReadiness || "No summary available."}</p>
              </div>
            </div>
          </div>
        )

      default:
        return <div>Section not found</div>
    }
  }

  const handleDownloadPDF = () => {
    const element = document.createElement("a")
    const file = new Blob(
      [`Department Analysis Report\n\nGenerated on: ${new Date().toLocaleDateString()}`],
      { type: "text/plain" }
    );
    element.href = URL.createObjectURL(file)
    element.download = `department-analysis-report-${Date.now()}.txt`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const generateDefaultFilename = () => {
    const date = new Date()
    const dateStr = date.toISOString().split("T")[0]
    return `department-analysis-report-${dateStr}`
  }

  const handleSaveReport = () => {
    const defaultName = generateDefaultFilename()
    setFilename(defaultName)
    setShowFilenameModal(true)
  }

  const confirmSaveReport = () => {
    if (!filename.trim()) {
      alert("Please enter a filename")
      return
    }
    setShowFilenameModal(false)
    setIsSaving(true)
    setTimeout(() => {
      setIsSaving(false)
      setShowSaveModal(true)
    }, 1500)
  }

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId)
    setActiveSubTab("analysis")
    setActiveSkillTab("softSkills")
  }

  return (
    <div className="dept-detailed-comparison-page">
      <BackButton />

      <main className="detailed-comparison-main">
        <div className="container">
          {/* Header */}
          <div className="detailed-comparison-header">
            <div className="detailed-header-content">
              <div className="detailed-header-text">
                <h1>{departmentData.title}</h1>
                <p>Comprehensive aggregate gap and analysis</p>
              </div>
              <div className="detailed-header-actions" style={{display: "none"}}>
                <button className="detailed-action-btn detailed-secondary" onClick={handleDownloadPDF}>
                  <Download size={16} />
                  Download PDF
                </button>
                <button className="detailed-action-btn detailed-primary" onClick={handleSaveReport} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="dept-detailed-comparison-layout">
            {/* Navigation Sidebar */}
            <div className="dept-detailed-navigation">
              <div className="dept-detailed-nav-header">
                <h3>Report Sections</h3>
              </div>
              <nav className="dept-detailed-nav-menu">
                {sections.map((section) => {
                  const IconComponent = section.icon
                  return (
                    <button
                      key={section.id}
                      className={`dept-detailed-nav-item ${activeSection === section.id ? "active" : ""}`}
                      onClick={() => handleSectionChange(section.id)}
                    >
                      <IconComponent size={18} />
                      <span>{section.label}</span>
                    </button>
                  )
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="dept-detailed-content">
              <div className="dept-detailed-content-header">
                <h2>{sections.find((s) => s.id === activeSection)?.label}</h2>
              </div>
              {renderSection()}
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {showFilenameModal && (
        <Modal
          show={showFilenameModal}
          onClose={() => setShowFilenameModal(false)}
          type="info"
          title="Save Report"
          showButton={false}
        >
          <div className="dept-detailed-filename-modal-content">
            <div className="dept-detailed-form-group">
              <label htmlFor="filename">Report Filename</label>
              <input
                type="text"
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="Enter filename..."
                className="dept-detailed-filename-input"
                autoFocus
              />
            </div>
            <div className="dept-detailed-modal-actions">
              <button
                className="dept-detailed-modal-btn dept-detailed-secondary"
                onClick={() => setShowFilenameModal(false)}
              >
                Cancel
              </button>
              <button
                className="dept-detailed-modal-btn dept-detailed-primary"
                onClick={confirmSaveReport}
                disabled={!filename.trim()}
              >
                Save Report
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showSaveModal && (
        <Modal
          show={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          type="success"
          title="Report Saved Successfully!"
          message={`Your department analysis report "${filename}" has been saved.`}
          showButton={true}
        />
      )}
    </div>
  )
}

export default DepartmentDetailedReport